import {
  X,
  User,
  Mail,
  Phone,
  MessageSquare,
  Lock,
  Hash,
  Webhook,
} from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";

const UserModal = ({ isOpen, onClose, onSave, activeUser, user = null }) => {
  const modalRef = useRef(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [showToken, setShowToken] = useState(false);
  const [copied, setCopied] = useState(false);

  const [formData, setFormData] = useState({
    avatar: null,
    name: "",
    email: "",
    password: "",
    phoneNumber: "",
    department: "",
    role: "USER",
    ssoToken: false,
  });

  const [errors, setErrors] = useState({});

  const handleOverlayClick = (event) => {
    if (modalRef.current && !modalRef.current.contains(event.target)) {
      onClose();
    }
  };

  useEffect(() => {
    const handleEsc = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    if (isOpen) {
      setShowToken(false);
      setCopied(false);
      window.addEventListener("keydown", handleEsc);
    }
    return () => {
      window.removeEventListener("keydown", handleEsc);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (user?.avatar) {
      setImagePreview(process.env.REACT_APP_URL + user.avatar);
    } else {
      setImagePreview(null);
    }

    if (user) {
      setFormData({
        avatar: user.avatar || null,
        name: user.name || "",
        email: user.email || "",
        password: "",
        phoneNumber: user.phoneNumber || "",
        department: user.department || "",
        role: user.role || "USER",
        ssoToken: user.ssoToken || false,
      });
    } else {
      setFormData({
        avatar: null,
        name: "",
        email: "",
        password: "",
        phoneNumber: "",
        department: "",
        role: "USER",
        ssoToken: false,
      });
    }
    setErrors({});
  }, [user, isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      alert("Selecione um arquivo de imagem válido");
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      alert("A imagem deve ter no máximo 2MB");
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
      setFormData((prev) => ({
        ...prev,
        avatar: file,
      }));
    };

    reader.readAsDataURL(file);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) newErrors.name = "Nome é obrigatório";
    if (!formData.email.trim()) newErrors.email = "Email é obrigatório";
    else if (!/\S+@\S+\.\S+/.test(formData.email))
      newErrors.email = "Email inválido";
    if (!user && !formData.password.trim())
      newErrors.password = "Senha é obrigatório";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const formattedPhoneNumber = `55${formData.phoneNumber.trim()}`;

    const updatedFormData = {
      ...formData,
      phoneNumber: formattedPhoneNumber,
    };

    if (validateForm()) {
      const payload = new FormData();

      Object.entries(updatedFormData).forEach(([key, value]) => {
        payload.append(key, value);
      });

      onSave(payload);
      onClose();
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          key="overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          onMouseDown={handleOverlayClick}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
        >
          <motion.div
            ref={modalRef}
            key="modal"
            initial={{ opacity: 0, scale: 0.9, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="relative flex max-h-[90vh] w-full max-w-2xl flex-col rounded-lg bg-white shadow-xl dark:bg-gray-900"
          >
            {/* Header */}
            <div className="flex items-center justify-between border-b border-gray-200 p-6 dark:border-gray-700">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                {user ? "Editar Usuário" : "Novo Usuário"}
              </h2>
              <button
                onClick={onClose}
                className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-800 dark:hover:text-gray-300"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Form */}
            <form
              onSubmit={handleSubmit}
              className="flex-1 overflow-y-auto p-6"
            >
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div className="flex flex-col items-center justify-center">
                  <label className="relative cursor-pointer transition-opacity hover:opacity-80">
                    <div className="flex h-28 w-28 items-center justify-center overflow-hidden rounded-full border-2 border-dashed border-gray-300 bg-gray-50 dark:border-gray-600 dark:bg-gray-800">
                      {imagePreview ? (
                        <img
                          src={imagePreview}
                          alt="Preview"
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <span className="px-2 text-center text-xs text-gray-500">
                          Carregar foto
                        </span>
                      )}
                    </div>

                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="hidden"
                    />
                  </label>

                  {imagePreview && (
                    <button
                      type="button"
                      onClick={() => {
                        setImagePreview(null);
                        setFormData((prev) => ({ ...prev, avatar: null }));
                      }}
                      className="text-xs font-medium text-gray-500"
                    >
                      Remover
                    </button>
                  )}
                </div>

                {/* Nome */}
                <div className="my-auto">
                  <label className="mb-2 flex items-center text-sm font-medium text-gray-700 dark:text-gray-300">
                    <User className="mr-2 h-4 w-4" />
                    Nome Completo *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className={`w-full rounded-lg border ${
                      errors.name
                        ? "border-red-500"
                        : "border-gray-300 dark:border-gray-600"
                    } bg-white px-4 py-2 text-gray-900 transition-colors focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-gray-100`}
                    placeholder="Ex: João Silva"
                  />
                  {errors.name && (
                    <p className="mt-1 text-sm text-red-500">{errors.name}</p>
                  )}
                </div>

                {activeUser?.role === "ADMIN" && (
                  <>
                    {/* Tipo de Usuário */}
                    <div className="flex flex-col gap-3">
                      <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300">
                        <User className="mr-2 h-4 w-4" />
                        Tipo de Usuário
                      </label>

                      <div className="flex items-center gap-3">
                        <span className="text-gray-700 dark:text-gray-300">
                          Usuário
                        </span>

                        <button
                          type="button"
                          onClick={() =>
                            setFormData((prev) => ({
                              ...prev,
                              role: prev.role === "ADMIN" ? "USER" : "ADMIN",
                            }))
                          }
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                            formData.role === "ADMIN"
                              ? "bg-green-500"
                              : "bg-gray-300 dark:bg-gray-700"
                          }`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              formData.role === "ADMIN"
                                ? "translate-x-6"
                                : "translate-x-1"
                            }`}
                          />
                        </button>

                        <span className="text-gray-700 dark:text-gray-300">
                          Admin
                        </span>
                      </div>
                    </div>

                    {/* Token SSO */}
                    <div className="flex flex-col gap-3">
                      <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300">
                        <Hash className="mr-2 h-4 w-4" />
                        Gerar Token SSO
                      </label>

                      <div className="flex items-center gap-3">
                        <span className="text-gray-700 dark:text-gray-300">
                          Negativo
                        </span>

                        <button
                          type="button"
                          onClick={() =>
                            setFormData((prev) => ({
                              ...prev,
                              ssoToken: !prev.ssoToken,
                            }))
                          }
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                            formData.ssoToken
                              ? "bg-green-500"
                              : "bg-gray-300 dark:bg-gray-700"
                          }`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              formData.ssoToken
                                ? "translate-x-6"
                                : "translate-x-1"
                            }`}
                          />
                        </button>

                        <span className="text-gray-700 dark:text-gray-300">
                          Positivo
                        </span>
                      </div>
                    </div>

                    {user?.ssoToken && (
                      <div className="col-span-2 flex flex-col gap-2">
                        <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300">
                          <Webhook className="mr-2 h-4 w-4" />
                          Token SSO
                        </label>

                        <div className="flex w-full max-w-full flex-wrap gap-2">
                          <input
                            type={showToken ? "text" : "password"}
                            value={user.ssoToken}
                            readOnly
                            className="w-full rounded-lg border border-gray-300 bg-gray-100 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 md:flex-1"
                          />

                          <button
                            type="button"
                            onClick={() => setShowToken((prev) => !prev)}
                            className="whitespace-nowrap rounded-lg bg-green-500 px-3 py-2 text-sm text-white hover:bg-green-600"
                          >
                            {showToken ? "Ocultar" : "Mostrar"}
                          </button>

                          <button
                            type="button"
                            onClick={async () => {
                              await navigator.clipboard.writeText(
                                user.ssoToken,
                              );
                              setCopied(true);
                              setTimeout(() => setCopied(false), 2000);
                            }}
                            className="whitespace-nowrap rounded-lg bg-blue-500 px-3 py-2 text-sm text-white hover:bg-blue-600"
                          >
                            Copiar
                          </button>
                        </div>

                        {copied && (
                          <p className="text-xs text-green-500">
                            Token copiado para a área de transferência
                          </p>
                        )}
                      </div>
                    )}
                  </>
                )}

                {/* Email */}
                <div>
                  <label className="mb-2 flex items-center text-sm font-medium text-gray-700 dark:text-gray-300">
                    <Mail className="mr-2 h-4 w-4" />
                    Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`w-full rounded-lg border ${
                      errors.email
                        ? "border-red-500"
                        : "border-gray-300 dark:border-gray-600"
                    } bg-white px-4 py-2 text-gray-900 transition-colors focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-gray-100`}
                    placeholder="joao@exemplo.com"
                  />
                  {errors.email && (
                    <p className="mt-1 text-sm text-red-500">{errors.email}</p>
                  )}
                </div>

                {/* Senha */}
                <div>
                  <label className="mb-2 flex items-center text-sm font-medium text-gray-700 dark:text-gray-300">
                    <Lock className="mr-2 h-4 w-4" />
                    Senha *
                  </label>
                  <input
                    type="password"
                    name="password"
                    value={formData.password || ""}
                    onChange={handleChange}
                    className={`w-full rounded-lg border ${
                      errors.password
                        ? "border-red-500"
                        : "border-gray-300 dark:border-gray-600"
                    } bg-white px-4 py-2 text-gray-900 transition-colors focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-gray-100`}
                    placeholder="•••••••••"
                  />
                  {errors.password && (
                    <p className="mt-1 text-sm text-red-500">
                      {errors.password}
                    </p>
                  )}
                </div>

                {/* Telefone */}
                <div>
                  <label className="mb-2 flex items-center text-sm font-medium text-gray-700 dark:text-gray-300">
                    <Phone className="mr-2 h-4 w-4" />
                    Telefone
                  </label>
                  <input
                    type="tel"
                    name="phoneNumber"
                    value={formData.phoneNumber.replace(/\D/g, "")}
                    onChange={handleChange}
                    className={`w-full rounded-lg border ${
                      errors.phoneNumber
                        ? "border-red-500"
                        : "border-gray-300 dark:border-gray-600"
                    } bg-white px-4 py-2 text-gray-900 transition-colors focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-gray-100`}
                    placeholder="(11) 8765-4321"
                  />
                  {errors.phoneNumber && (
                    <p className="mt-1 text-sm text-red-500">
                      {errors.phoneNumber}
                    </p>
                  )}
                </div>

                {/* Departamento */}
                <div>
                  <label className="mb-2 flex items-center text-sm font-medium text-gray-700 dark:text-gray-300">
                    <MessageSquare className="mr-2 h-4 w-4" />
                    Departamento
                  </label>
                  <input
                    type="text"
                    name="department"
                    value={formData.department}
                    onChange={handleChange}
                    className={`w-full rounded-lg border ${
                      errors.department
                        ? "border-red-500"
                        : "border-gray-300 dark:border-gray-600"
                    } bg-white px-4 py-2 text-gray-900 transition-colors focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-gray-100`}
                    placeholder="Ex: Vendas, Suporte"
                  />
                  {errors.department && (
                    <p className="mt-1 text-sm text-red-500">
                      {errors.department}
                    </p>
                  )}
                </div>
              </div>

              {/* Footer */}
              <div className="mt-6 flex justify-end space-x-3 border-t border-gray-200 pt-6 dark:border-gray-700">
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="rounded-lg bg-green-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-green-600"
                >
                  {user ? "Salvar Alterações" : "Adicionar Usuário"}
                </button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default UserModal;
